// ────────────────────────────────────────────────────────────────────────────
// The journey a multi-stop order actually has to make, and where the SLACK is.
//
// Two questions, and the second is the interesting one:
//   1. Can this be delivered on time at all?
//   2. Where is the waiting, and what would remove it?
//
// Waiting is the thing worth optimising, and it comes in two kinds with
// different fixes. Sitting at the port because the collection arrived hours
// before check-in is fixed by taking a LATER sailing. Sitting at the
// destination because the ferry lands at 11:00, the drive is an hour and the
// delivery is booked for 15:00 is fixed by moving the DELIVERY EARLIER (or
// again, a later sailing). Reporting a single "on schedule" hides both.
//
// All arithmetic is in absolute minutes from a base midnight, so a crossing
// that rolls past midnight or a multi-day haul compares correctly against a
// deadline on a different day. Same convention as `bookings/lib/bookingDetail`,
// which is where the backwards calculation was first worked out.
// ────────────────────────────────────────────────────────────────────────────

/** Minutes in a day — the unit the absolute clock is built on. */
export const DAY = 1440;

// The geometry and the road-time fallback live in `utils/roadDistance`, shared
// with the bookings cockpit and the manifest so no two surfaces can quote
// different drive times for the same road. Re-exported here because this
// module is the engine callers already import.
export {
  distanceKm,
  estimatedRoadKm,
  roadMinutesFor,
  ROAD_WINDING_FACTOR,
} from '../../../utils/roadDistance';
import { toLandmass } from '../../bookings/lib/crossing';
import { estimatedRoadKm, roadMinutesFor } from '../../../utils/roadDistance';

// ── Inputs ────────────────────────────────────────────────────────────────

/** A stop on the order, reduced to what the plan needs. */
export interface PlanStop {
  /** Operator-facing label, e.g. "Carbery Group". */
  label: string;
  countryIsoCode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  /**
   * The stop's Google place id, ONLY when its coordinates came from Google —
   * routed legs are then cached by the place, not by Google coordinates.
   * See `roadLegEnd()` in @my/directory-api.
   */
  placeId?: string | null;
  /** The Directory address, as a label on the cached leg. */
  addressId?: string | null;
  /** ISO date, or null when the operator has not committed to one. */
  plannedDate?: string | null;
  /** "HH:mm", only meaningful when the mode is Exact. */
  plannedTime?: string | null;
  timeMode?: 'Undefined' | 'AM' | 'PM' | 'Exact' | null;
}

/** A sea crossing, from the route catalogue or an existing booking. */
export interface PlanCrossing {
  routeName: string;
  originPortName: string;
  destinationPortName: string;
  crossingMinutes: number;
  checkInMinutes: number;
  driveOffMinutes: number;
  /** Set when a booking exists — "HH:mm" of the actual sailing. */
  sailTime?: string | null;
  sailDate?: string | null;
  /** Customs applies on the crossing, not the road legs. */
  customs?: boolean;
  /**
   * True when this is a CANDIDATE crossing rather than a booking.
   *
   * <p>
   * An order whose stops change country needs a boat whether or not anyone has
   * booked one, and the unbooked case is the one worth planning: it is where
   * "the latest sailing you could still take" is a decision rather than a
   * record. The durations then come from the route catalogue's typicals, so
   * every time downstream is provisional too and the panel says so.
   * </p>
   */
  provisional?: boolean;
}

/**
 * A real road leg, from a routing service rather than a straight line.
 *
 * <p>
 * Injected as a seam so the plan never has to know where the number came from.
 * The intended source is **Azure Maps** route directions rather than Google:
 * Azure supports goods-vehicle routing — weight, height, length, axle load and
 * hazmat load type — so an ADR load in a 4m-high artic gets a lawful route
 * instead of a car's. Those parameters are already on the order (the resource's
 * dimensions, the ADR / haz service tags), so the estimate is the only part
 * that has to be temporary.
 * </p>
 */
export interface RoadLeg {
  minutes: number;
  km?: number;
  /** False once a routing service supplied it — drives the "~" in the UI. */
  estimated: boolean;
}

/**
 * Resolves a road leg between two points. Returning null falls back to the
 * great-circle estimate, so an unavailable routing service degrades the
 * numbers rather than blanking the panel.
 */
export type RoadLegResolver = (
  from: { latitude?: number | null; longitude?: number | null },
  to: { latitude?: number | null; longitude?: number | null },
) => RoadLeg | null;

export interface PlanInput {
  stops: PlanStop[];
  crossing?: PlanCrossing | null;
  /** Port coordinates, when known, for the road legs either side. */
  originPort?: { latitude?: number | null; longitude?: number | null } | null;
  destinationPort?: { latitude?: number | null; longitude?: number | null } | null;
  /** Day 0 of the absolute clock — the earliest planned date on the order. */
  baseDate: string;
  /**
   * Real road times, when a routing service has them. Absent or returning null
   * for a pair falls back to the great-circle estimate.
   */
  resolveRoad?: RoadLegResolver;
}

// ── Time-window handling ──────────────────────────────────────────────────

/**
 * The LATEST minute a stop can be served, and the EARLIEST it can be.
 *
 * A window is not a time, and collapsing it to one is how a plan starts lying.
 * `AM` means "any time before noon", so the deadline is 12:00 but the earliest
 * useful arrival is 08:00 — arriving at 06:00 means two hours of waiting, which
 * is exactly what this is meant to surface.
 */
export function stopWindow(
  stop: PlanStop,
  dayOffset: number,
): { earliest: number | null; latest: number | null } {
  if (!stop.plannedDate) return { earliest: null, latest: null };
  const base = dayOffset * DAY;
  switch (stop.timeMode) {
    case 'Exact': {
      const at = parseHM(stop.plannedTime);
      return at == null ? { earliest: null, latest: null } : { earliest: base + at, latest: base + at };
    }
    case 'AM':
      return { earliest: base + 8 * 60, latest: base + 12 * 60 };
    case 'PM':
      return { earliest: base + 12 * 60, latest: base + 18 * 60 };
    default:
      // No commitment: treat the whole working day as acceptable, so an
      // unscheduled stop never reports false waiting.
      return { earliest: base + 8 * 60, latest: base + 18 * 60 };
  }
}

/** "HH:mm" to minutes past midnight, or null. */
export function parseHM(value: string | null | undefined): number | null {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** Whole days from the base date to an ISO date, or null. */
export function dayOffsetFrom(baseDate: string, iso: string | null | undefined): number | null {
  if (!iso) return null;
  const a = Date.parse(`${baseDate}T00:00:00Z`);
  const b = Date.parse(`${iso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

// ── Output ────────────────────────────────────────────────────────────────

/**
 * `wait` and `hold` are both standing still, and are deliberately different
 * things: a `wait` is time nobody chose — the boat is not there yet — and is
 * worth removing. A `hold` is the plan choosing to keep the load at the depot
 * rather than send it to sit on the far side, and is worth nothing but a label.
 */
export type SegmentKind =
  | 'road'
  | 'checkIn'
  | 'crossing'
  | 'customs'
  | 'driveOff'
  | 'wait'
  | 'hold';

export interface PlanSegment {
  kind: SegmentKind;
  minutes: number;
  /** Estimated rather than known — a road leg derived from coordinates. */
  estimated?: boolean;
}

export interface PlanPoint {
  label: string;
  /** A stop the order serves, or a ferry port it passes through. */
  kind: 'stop' | 'port';
  /** Absolute minutes. Null when nothing upstream was committed. */
  atMinutes: number | null;
  /** The window's deadline, where the stop has one. */
  dueMinutes?: number | null;
  /**
   * The legs leading INTO this point, in order.
   *
   * <p>
   * The flat `segments` list cannot be walked alongside `points`: between two
   * ports sit check-in, waiting, the crossing, drive-off and customs, so the
   * two indexes drift apart the moment a crossing exists. Grouping the legs per
   * point is what lets a timeline render both in step.
   * </p>
   */
  legs: PlanSegment[];
}

export type PlanVerdict = 'onTime' | 'tight' | 'late' | 'unknown';

export interface PlanSuggestion {
  kind:
    | 'moveDeliveryEarlier'
    | 'takeLaterSailing'
    | 'cannotMakeIt'
    | 'cannotReachSailing'
    | 'slack';
  /** Minutes of waiting this would remove, or of lateness to recover. */
  minutes: number;
  /** Absolute minute to move a stop to, for the actionable suggestions. */
  toMinutes?: number;
  message: string;
}

export interface JourneyPlan {
  /**
   * Day zero of the absolute clock, echoed back from the input.
   *
   * <p>
   * Without it nothing downstream can turn a minute count into a date, and a
   * timeline of bare clock times is actively misleading on a multi-day haul —
   * "due 18:00" against a delivery four days out reads as tonight.
   * </p>
   */
  baseDate: string;
  needsCrossing: boolean;
  /**
   * True when the crossing in the timeline is a candidate rather than a
   * booking — every time after the departure port is then a proposal.
   */
  crossingProvisional: boolean;
  countryChange: { from: string; to: string } | null;
  segments: PlanSegment[];
  points: PlanPoint[];
  /** Earliest achievable arrival at the final stop. */
  etaMinutes: number | null;
  /** Waiting at the port after check-in closes, before the boat leaves. */
  portWaitMinutes: number | null;
  /** Waiting at the destination before its window opens. */
  destinationWaitMinutes: number | null;
  /** How late the ETA is against the final window; 0 when on time. */
  lateByMinutes: number | null;
  /** Latest sailing departure that still meets the deadline. */
  latestDepartureMinutes: number | null;
  /** That, minus check-in — the number an operator books against. */
  latestIntoPortMinutes: number | null;
  /**
   * The latest the truck can set off from the collection and still make it —
   * the single most actionable number on an unbooked international order.
   */
  latestLeaveCollectionMinutes: number | null;
  /**
   * Slack held at the collection depot instead of on the far side. Not a
   * problem to be warned about: it is the plan.
   */
  depotWaitMinutes: number | null;
  /**
   * True when the times shown are the LATEST that still work rather than the
   * earliest achievable — see `buildJourneyPlan`.
   */
  plannedBackwards: boolean;
  verdict: PlanVerdict;
  suggestions: PlanSuggestion[];
  /** True where any road leg came from coordinates rather than a real route. */
  hasEstimates: boolean;
  /**
   * Minutes by which the collection misses the BOOKED sailing, or null.
   *
   * <p>
   * Checked because the alternative is a plan that sails on a boat which has
   * already gone: with a 12:00 collection and an 02:30 departure, using the
   * booked time unconditionally put the arrival BEFORE the collection and then
   * advised bringing the delivery forward. Rolling to the next day's sailing
   * would be worse — it would silently plan a different sailing from the one
   * that is booked. So this is reported as the conflict it is.
   * </p>
   */
  missesSailingByMinutes: number | null;
}

/**
 * A crossing is needed when consecutive stops are on different landmasses — not
 * merely in different countries. The first such pair is returned, so Cork →
 * Belfast → Liverpool reports XI→GB, the leg that actually sails.
 */
export function detectCountryChange(stops: PlanStop[]): { from: string; to: string } | null {
  for (let i = 1; i < stops.length; i++) {
    const from = stops[i - 1]?.countryIsoCode?.trim().toUpperCase();
    const to = stops[i]?.countryIsoCode?.trim().toUpperCase();
    // Landmass, not country: IE→XI (Cork→Belfast) is a drive, FR→DE a border.
    if (from && to && toLandmass(from) !== toLandmass(to)) return { from, to };
  }
  return null;
}

/** Threshold below which waiting is not worth an operator's attention. */
const WAIT_WORTH_MENTIONING = 45;

/** Below this much spare time, "on time" is optimistic. */
const TIGHT_MARGIN = 60;

/** Customs clearance allowance on a crossing that needs it. */
const CUSTOMS_MINUTES = 45;

/**
 * How far ahead of the delivery deadline the backwards plan aims to arrive.
 *
 * <p>
 * The user's words: the last booking that makes it "in good time (like 30min /
 * 1hour before or something like that)". Planning to arrive exactly as the
 * window shuts is not a plan, it is a coin toss — so the whole backwards chain
 * hangs off the deadline minus this.
 * </p>
 */
const ARRIVAL_BUFFER_MINUTES = 60;

/**
 * Build the plan.
 *
 * <p>
 * Forwards from the collection to get the ETA, then backwards from the deadline
 * to get the latest sailing. Both directions are needed: forwards answers "when
 * would we actually arrive", and therefore where the waiting is; backwards
 * answers "how late could we leave", and therefore which sailings are still
 * bookable. Computing only one is how a plan reports "on schedule" while a
 * driver sits at a port for four hours.
 * </p>
 *
 * <p>
 * WHICH ONE IS SHOWN depends on whether a sailing is booked. A booked sailing
 * is a fact, so the plan runs forwards through it and any waiting is real.
 * With nothing booked, the earliest arrival is the WRONG answer: it puts the
 * truck on the far side days before anyone will unload it. The user's rule —
 * "it would be easier to delay the truck at the depot than at the other side
 * for 2 days" — so the times shown are then the LATEST that still deliver in
 * good time, and the slack is left at home where it costs nothing.
 * </p>
 */
export function buildJourneyPlan(input: PlanInput): JourneyPlan {
  const { stops, crossing, baseDate } = input;
  const first = stops[0];
  const last = stops[stops.length - 1];
  const countryChange = detectCountryChange(stops);

  const segments: PlanSegment[] = [];
  const points: PlanPoint[] = [];
  // Legs accumulated since the last point, handed to the next one. A leg of no
  // minutes is dropped rather than rendered: a catalogue with no typical
  // check-in should show nothing, not "0m check-in".
  let pending: PlanSegment[] = [];
  const addLeg = (segment: PlanSegment) => {
    if (segment.minutes <= 0) return;
    segments.push(segment);
    pending.push(segment);
  };
  const addPoint = (point: Omit<PlanPoint, 'legs'>) => {
    points.push({ ...point, legs: pending });
    pending = [];
  };
  let hasEstimates = false;
  let missesSailingBy: number | null = null;
  // Hoisted: the backwards pass below needs the same leg durations the forward
  // pass used, and recomputing them risks the two disagreeing.
  let bookedDeparture: number | null = null;
  let toPortLeg: RoadLeg | null = null;
  let fromPortLeg: RoadLeg | null = null;
  let directLeg: RoadLeg | null = null;

  const startOffset = dayOffsetFrom(baseDate, first?.plannedDate) ?? 0;
  const endOffset = dayOffsetFrom(baseDate, last?.plannedDate) ?? startOffset;
  const startWindow = first ? stopWindow(first, startOffset) : { earliest: null, latest: null };
  const endWindow = last ? stopWindow(last, endOffset) : { earliest: null, latest: null };

  // One place decides how long a road leg takes: a routing service if one is
  // wired up, a straight-line estimate otherwise. Every caller below goes
  // through here so the two cannot disagree within a single plan.
  const roadLeg = (
    a: { latitude?: number | null; longitude?: number | null } | null | undefined,
    b: { latitude?: number | null; longitude?: number | null } | null | undefined,
  ): RoadLeg | null => {
    if (!a || !b) return null;
    const routed = input.resolveRoad?.(a, b);
    if (routed) return routed;
    const km = estimatedRoadKm(a, b);
    return km == null ? null : { minutes: roadMinutesFor(km), km, estimated: true };
  };

  // Leave the collection at the LATEST its window allows. Assuming the earliest
  // would invent slack that is really just an unstated choice about when to set
  // off, and the point of this is to show slack that is actually there.
  let cursor = startWindow.latest;
  addPoint({
    label: first?.label ?? 'Collection',
    kind: 'stop',
    atMinutes: cursor,
    dueMinutes: startWindow.latest,
  });

  let portWait: number | null = null;

  if (!crossing) {
    // Road only. Still worth planning: the ETA against the delivery window is
    // what shows whether the day is over- or under-committed.
    const direct = (directLeg = roadLeg(first, last));
    if (direct == null) {
      cursor = null;
    } else {
      addLeg({ kind: 'road', minutes: direct.minutes, estimated: direct.estimated });
      hasEstimates = hasEstimates || direct.estimated;
      if (cursor != null) cursor += direct.minutes;
    }
  } else {
    const customs = crossing.customs ? CUSTOMS_MINUTES : 0;

    // Collection → departure port.
    const toPort = (toPortLeg = roadLeg(first, input.originPort));
    if (toPort == null) {
      cursor = null;
    } else {
      addLeg({ kind: 'road', minutes: toPort.minutes, estimated: toPort.estimated });
      hasEstimates = hasEstimates || toPort.estimated;
      if (cursor != null) cursor += toPort.minutes;
    }
    addPoint({ label: crossing.originPortName, kind: 'port', atMinutes: cursor });

    // A booked sailing has a real departure. An unbooked one is planned as the
    // earliest we could catch: arrive, clear check-in, go.
    const sailOffset = dayOffsetFrom(baseDate, crossing.sailDate);
    const sailAt = parseHM(crossing.sailTime);
    bookedDeparture = sailAt != null && sailOffset != null ? sailOffset * DAY + sailAt : null;
    const readyToBoard = cursor == null ? null : cursor + crossing.checkInMinutes;
    const departure = bookedDeparture ?? readyToBoard;

    if (bookedDeparture != null && readyToBoard != null && bookedDeparture < readyToBoard) {
      missesSailingBy = readyToBoard - bookedDeparture;
    }

    portWait =
      bookedDeparture != null && readyToBoard != null
        ? Math.max(0, bookedDeparture - readyToBoard)
        : 0;

    addLeg({ kind: 'checkIn', minutes: crossing.checkInMinutes });
    if (portWait > 0) addLeg({ kind: 'wait', minutes: portWait });
    addLeg({ kind: 'crossing', minutes: crossing.crossingMinutes });

    // A missed sailing has no honest arrival time — anything computed from a
    // departure the truck cannot reach is fiction, so the chain stops here and
    // downstream times report as unknown.
    cursor =
      departure == null || missesSailingBy != null
        ? null
        : departure + crossing.crossingMinutes;
    addPoint({ label: crossing.destinationPortName, kind: 'port', atMinutes: cursor });

    if (crossing.driveOffMinutes > 0) {
      addLeg({ kind: 'driveOff', minutes: crossing.driveOffMinutes });
      if (cursor != null) cursor += crossing.driveOffMinutes;
    }
    if (customs > 0) {
      addLeg({ kind: 'customs', minutes: customs });
      if (cursor != null) cursor += customs;
    }

    // Arrival port → delivery. The number the whole thing turns on: "a delivery
    // an hour from the port" is this leg.
    const fromPort = (fromPortLeg = roadLeg(input.destinationPort, last));
    if (fromPort == null) {
      cursor = null;
    } else {
      addLeg({ kind: 'road', minutes: fromPort.minutes, estimated: fromPort.estimated });
      hasEstimates = hasEstimates || fromPort.estimated;
      if (cursor != null) cursor += fromPort.minutes;
    }
  }

  const earliestArrival = cursor;
  addPoint({
    label: last?.label ?? 'Delivery',
    kind: 'stop',
    atMinutes: earliestArrival,
    dueMinutes: endWindow.latest,
  });

  // Feasibility is judged on the EARLIEST arrival, always. It is the only thing
  // that answers "can this be done at all", and the backwards plan below must
  // not be allowed to claim a delivery the truck could never reach.
  const impossibleBy =
    earliestArrival != null && endWindow.latest != null
      ? Math.max(0, earliestArrival - endWindow.latest)
      : null;

  // The two numbers an operator books against.
  //
  // A booked sailing sets its own deadline: be at the port by check-in. With
  // nothing booked they come from the delivery instead — the latest sailing
  // that still lands, clears and drives in with the arrival buffer intact.
  let latestDeparture: number | null = null;
  let latestIntoPort: number | null = null;
  const afterTheBoat = crossing
    ? crossing.crossingMinutes +
      crossing.driveOffMinutes +
      (crossing.customs ? CUSTOMS_MINUTES : 0) +
      (fromPortLeg?.minutes ?? 0)
    : 0;
  if (crossing && bookedDeparture != null) {
    latestDeparture = bookedDeparture;
    latestIntoPort = bookedDeparture - crossing.checkInMinutes;
  } else if (crossing && endWindow.latest != null) {
    latestDeparture = endWindow.latest - ARRIVAL_BUFFER_MINUTES - afterTheBoat;
    latestIntoPort = latestDeparture - crossing.checkInMinutes;
  }

  // ── The latest plan that still works ──────────────────────────────────────
  // ALWAYS the direction of choice, crossing or not: a road-only order sitting
  // overnight at the far end is the same waste as a truck parked at a port, and
  // has the same fix. Only a BOOKED sailing overrides it, because that time is
  // a fact rather than a choice.
  //
  // Requires a reachable delivery (never let arithmetic claim an arrival the
  // truck could not make), a deadline to work back from, and genuine slack. The
  // collection keeps its own window — the load is picked up when the task says
  // — so the slack lands on the leg AFTER it, as a hold before setting off.
  let plannedBackwards = false;
  let latestLeaveCollection: number | null = null;
  let depotWait: number | null = null;
  const canReplan =
    bookedDeparture == null &&
    endWindow.latest != null &&
    startWindow.latest != null &&
    (impossibleBy ?? 0) === 0;
  const targetArrival = endWindow.latest == null ? null : endWindow.latest - ARRIVAL_BUFFER_MINUTES;

  if (canReplan && targetArrival != null && startWindow.latest != null) {
    // The whole chain back to "when does the truck leave", per journey shape.
    const chain =
      crossing && latestIntoPort != null && latestDeparture != null && toPortLeg && fromPortLeg
        ? {
            leaveCollection: latestIntoPort - toPortLeg.minutes,
            // [collection, departure port, arrival port, delivery]
            times: [
              startWindow.latest,
              latestIntoPort,
              latestDeparture + crossing.crossingMinutes,
              targetArrival,
            ],
          }
        : !crossing && directLeg
          ? {
              leaveCollection: targetArrival - directLeg.minutes,
              times: [startWindow.latest, targetArrival],
            }
          : null;

    const slack = chain == null ? 0 : chain.leaveCollection - startWindow.latest;
    if (chain != null && slack > 0 && chain.times.length === points.length) {
      plannedBackwards = true;
      depotWait = slack;
      latestLeaveCollection = chain.leaveCollection;
      chain.times.forEach((at, i) => {
        points[i]!.atMinutes = at;
      });
      // Shown where the waiting should actually be taken. Inserted at the front
      // of both lists so `segments` stays the flattening of `points[].legs`.
      const held: PlanSegment = { kind: 'hold', minutes: slack };
      points[1]!.legs.unshift(held);
      segments.unshift(held);
    }
  }

  // The LAST point, not a fixed index: a road-only journey has two of them and
  // a crossing has four. (Hardcoding 3 here worked for every crossing test and
  // threw on the first domestic order.)
  const etaMinutes = plannedBackwards
    ? (points[points.length - 1]?.atMinutes ?? earliestArrival)
    : earliestArrival;

  // Waiting at the destination: arriving before anyone will receive it. Zero by
  // construction once the plan plans backwards, which is the whole point.
  const destinationWait =
    etaMinutes != null && endWindow.earliest != null
      ? Math.max(0, endWindow.earliest - etaMinutes)
      : null;

  const lateBy = plannedBackwards ? 0 : impossibleBy;

  const verdict: PlanVerdict =
    missesSailingBy != null
      ? 'late'
      : plannedBackwards
        ? // Built to arrive a buffer early, so it cannot be anything else. Said
          // outright rather than left to fall out of the margin comparison,
          // where a buffer smaller than TIGHT_MARGIN would read as "tight".
          'onTime'
        : etaMinutes == null || endWindow.latest == null
          ? 'unknown'
          : lateBy != null && lateBy > 0
            ? 'late'
            : endWindow.latest - etaMinutes < TIGHT_MARGIN
              ? 'tight'
              : 'onTime';

  return {
    baseDate,
    needsCrossing: countryChange !== null,
    crossingProvisional: !!crossing?.provisional,
    countryChange,
    segments,
    points,
    etaMinutes,
    portWaitMinutes: portWait,
    destinationWaitMinutes: destinationWait,
    lateByMinutes: lateBy,
    latestDepartureMinutes: latestDeparture,
    latestIntoPortMinutes: latestIntoPort,
    latestLeaveCollectionMinutes: latestLeaveCollection,
    depotWaitMinutes: depotWait,
    plannedBackwards,
    verdict,
    suggestions: suggest({
      etaMinutes,
      destinationWait,
      portWait,
      lateBy,
      missesSailingBy,
      plannedBackwards,
      hasCrossing: crossing != null,
    }),
    hasEstimates,
    missesSailingByMinutes: missesSailingBy,
  };
}

function suggest(args: {
  etaMinutes: number | null;
  destinationWait: number | null;
  portWait: number | null;
  lateBy: number | null;
  missesSailingBy: number | null;
  plannedBackwards: boolean;
  hasCrossing: boolean;
}): PlanSuggestion[] {
  const out: PlanSuggestion[] = [];

  // The booked sailing cannot be reached. Everything downstream is unknowable
  // and no optimisation advice applies — the booking itself is the problem.
  if (args.missesSailingBy != null) {
    return [
      {
        kind: 'cannotReachSailing',
        minutes: args.missesSailingBy,
        message:
          `The collection reaches the port ${fmtDuration(args.missesSailingBy)} after ` +
          `check-in closes for the booked sailing — move the collection earlier, or rebook.`,
      },
    ];
  }

  // Nothing else matters while the delivery cannot be met.
  if (args.lateBy != null && args.lateBy > 0) {
    out.push({
      kind: 'cannotMakeIt',
      minutes: args.lateBy,
      message: `Arrives ${fmtDuration(args.lateBy)} after the delivery window closes.`,
    });
    return out;
  }

  // A backwards plan has already put the slack at the depot and shown it there.
  // Advising someone to remove waiting the plan is not proposing is noise.
  if (args.plannedBackwards) return out;

  if (args.destinationWait != null && args.destinationWait >= DAY) {
    // Not waiting. Nobody stands at a delivery for a day and a half, so a gap
    // this size is slack in the schedule — and advising someone to "bring the
    // delivery forward 83 hours" about it is how a panel loses its reader.
    out.push({
      kind: 'slack',
      minutes: args.destinationWait,
      message:
        `${fmtSpan(args.destinationWait)} spare before the delivery window opens — ` +
        (args.hasCrossing
          ? 'a later collection or sailing would still make it.'
          : 'a later collection would still make it.'),
    });
  } else if (args.destinationWait != null && args.destinationWait >= WAIT_WORTH_MENTIONING) {
    out.push({
      kind: 'moveDeliveryEarlier',
      minutes: args.destinationWait,
      toMinutes: args.etaMinutes ?? undefined,
      message:
        `Arrives ${fmtDuration(args.destinationWait)} before the delivery window opens — ` +
        (args.hasCrossing
          ? 'bring the delivery forward, or take a later sailing.'
          : 'bring the delivery forward, or set off later.'),
    });
  }

  if (args.portWait != null && args.portWait >= WAIT_WORTH_MENTIONING) {
    out.push({
      kind: 'takeLaterSailing',
      minutes: args.portWait,
      message:
        `${fmtDuration(args.portWait)} waiting at the port after check-in — ` +
        `a later sailing would collect later and wait less.`,
    });
  }

  return out;
}

/** "3h 20m" / "45m" — the shape the booking surfaces already use. */
export function fmtDuration(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rest = m % 60;
  if (h === 0) return `${rest}m`;
  return rest === 0 ? `${h}h` : `${h}h ${String(rest).padStart(2, '0')}m`;
}

/**
 * "3d 11h" — for gaps longer than a day, where hours stop being readable.
 * Anything shorter falls through to `fmtDuration`.
 */
export function fmtSpan(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < DAY) return fmtDuration(m);
  const d = Math.floor(m / DAY);
  const h = Math.round((m % DAY) / 60);
  return h === 0 ? `${d}d` : `${d}d ${h}h`;
}

/** Absolute minutes to "HH:mm". */
export function fmtClock(abs: number): string {
  const m = ((Math.round(abs) % DAY) + DAY) % DAY;
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/**
 * "Sat 05" for an absolute minute — the day a step actually falls on.
 *
 * <p>
 * Built on UTC midnights, like `shiftInDays` in `utils/dates`: the clock here
 * counts minutes from a calendar day, not from an instant, so bringing a local
 * timezone into it would shift a step onto the wrong date for anyone east or
 * west of the org. Empty string for an unparseable base date.
 * </p>
 */
export function dayLabel(baseDate: string, abs: number): string {
  const base = Date.parse(`${baseDate}T00:00:00Z`);
  if (Number.isNaN(base)) return '';
  const day = new Date(base + dayIndexOf(abs) * 86_400_000);
  return `${WEEKDAYS[day.getUTCDay()]} ${String(day.getUTCDate()).padStart(2, '0')}`;
}

/** Whole days from the base date — for labelling a step that lands tomorrow. */
export function dayIndexOf(abs: number): number {
  return Math.floor(abs / DAY);
}
